import React, { useState, useRef, useEffect } from 'react';
import { useChatDocsStore, fileNameById } from './cdx-store';
import { AttachIcon, TrashIcon, SendIcon, LinkIcon } from './cdx-icons';
import { supabase } from '@/integrations/supabase/client';
import './cdx-style.css';

export const CDXChat: React.FC = () => {
  const { messages, isStreaming, sessionId, files, addMessage, updateMessage, setStreaming, clearChat } = useChatDocsStore();
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || isStreaming) return;

    const userMessage = input.trim();
    addMessage({ role: 'user', content: userMessage });
    setInput('');
    setStreaming(true);

    try {
      // Obter JWT do usuário autenticado
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        addMessage({ role: 'assistant', content: 'Erro: Usuário não autenticado' });
        setStreaming(false);
        return;
      }

      const SUPABASE_URL = 'https://jfdgdjxvzsmuwofclqyt.supabase.co';
      const SUPABASE_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImpmZGdkanh2enNtdXdvZmNscXl0Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDMwNDAwMDksImV4cCI6MjA1ODYxNjAwOX0.LdmCn6tAQE0J22dspr08r2WEStJphGBE4sZmcx6Fd9A';

      // 1) Chamar endpoint de chat para obter stream URL
      const chatRes = await fetch(`${SUPABASE_URL}/functions/v1/docchat-chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': SUPABASE_KEY
        },
        body: JSON.stringify({ 
          sessionId, 
          messages: [{ role: 'user', content: userMessage }] 
        })
      });

      if (!chatRes.ok) {
        const errorData = await chatRes.json();
        throw new Error(errorData.error || 'Erro ao processar mensagem');
      }

      const { streamUrl } = await chatRes.json();

      // 2) Abrir EventSource para streaming
      const es = new EventSource(streamUrl);

      // Criar mensagem vazia da IA
      const assistantMsgIndex = messages.length + 1;
      addMessage({ role: 'assistant', content: '', citations: [] });

      let accumulatedContent = '';

      // Evento: thread.message.delta (tokens)
      es.addEventListener('thread.message.delta', (e: MessageEvent) => {
        try {
          const data = JSON.parse(e.data);
          const delta = data?.delta?.content?.[0]?.text?.value ?? '';
          
          if (delta) {
            accumulatedContent += delta;
            updateMessage(assistantMsgIndex, { content: accumulatedContent });
          }
        } catch (err) {
          console.error('[CDX] Error parsing delta:', err);
        }
      });

      // Evento: thread.message.completed (citações)
      es.addEventListener('thread.message.completed', (e: MessageEvent) => {
        try {
          const data = JSON.parse(e.data);
          const annotations = data?.content?.[0]?.text?.annotations ?? [];
          
          const citations = annotations
            .filter((ann: any) => ann.file_citation)
            .map((ann: any) => ({
              fileName: fileNameById.get(ann.file_citation.file_id) || ann.file_citation.file_id,
              page: ann.file_citation.quote,
              snippet: ann.text
            }));
          
          if (citations.length > 0) {
            updateMessage(assistantMsgIndex, { citations });
          }
        } catch (err) {
          console.error('[CDX] Error parsing citations:', err);
        }
      });

      // Evento: response.completed
      es.addEventListener('response.completed', () => {
        console.log('[CDX] Stream completed');
        es.close();
        setStreaming(false);
      });

      // Evento: error
      es.addEventListener('error', (e) => {
        console.error('[CDX] Stream error:', e);
        es.close();
        setStreaming(false);
        updateMessage(assistantMsgIndex, { 
          content: accumulatedContent || 'Erro ao processar resposta.' 
        });
      });

    } catch (err: any) {
      console.error('[CDX] Send message error:', err);
      setStreaming(false);
      addMessage({ 
        role: 'assistant', 
        content: `Erro: ${err.message || 'Não foi possível processar sua mensagem.'}` 
      });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleClear = async () => {
    if (!confirm('Deseja limpar todo o histórico e arquivos da sessão?')) return;
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;

      const SUPABASE_URL = 'https://jfdgdjxvzsmuwofclqyt.supabase.co';
      const SUPABASE_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImpmZGdkanh2enNtdXdvZmNscXl0Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDMwNDAwMDksImV4cCI6MjA1ODYxNjAwOX0.LdmCn6tAQE0J22dspr08r2WEStJphGBE4sZmcx6Fd9A';

      await fetch(`${SUPABASE_URL}/functions/v1/docchat-clear`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': SUPABASE_KEY
        },
        body: JSON.stringify({ sessionId })
      });
      
      clearChat();
    } catch (err) {
      console.error('[CDX] Clear error:', err);
    }
  };

  const suggestedQuestions = [
    "Resuma os principais pontos deste documento",
    "Quais são as conclusões principais?",
    "Existe alguma informação sobre prazos?",
    "Qual é o contexto geral deste arquivo?"
  ];

  return (
    <div className="cdx-chat">
      <div className="cdx-messages" role="log" aria-live="polite">
        {messages.length === 0 ? (
          <div className="cdx-empty">
            <div className="cdx-empty-icon">💬</div>
            <p className="cdx-empty-text">
              {files.length === 0 
                ? "Faça uma pergunta ou envie arquivos para começar." 
                : "Pergunte algo sobre os arquivos enviados!"}
            </p>
            {files.length === 0 && (
              <div className="cdx-suggestions">
                {suggestedQuestions.map((q, i) => (
                  <button
                    key={i}
                    className="cdx-suggestion"
                    onClick={() => setInput(q)}
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          messages.map((msg, i) => (
            <div key={i} className={`cdx-msg cdx-msg--${msg.role}`}>
              <div className="cdx-msg-content">{msg.content}</div>
              {msg.citations && msg.citations.length > 0 && (
                <div className="cdx-citations">
                  {msg.citations.map((cite, j) => (
                    <span 
                      key={j} 
                      className="cdx-citation"
                      role="button"
                      tabIndex={0}
                      title={cite.snippet}
                    >
                      <LinkIcon />
                      Fonte: {cite.fileName}
                      {cite.page && ` (p. ${cite.page})`}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
        
        {isStreaming && (
          <div className="cdx-typing" aria-label="IA está digitando">
            <span className="cdx-dot"></span>
            <span className="cdx-dot"></span>
            <span className="cdx-dot"></span>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>
      
      <div className="cdx-input">
        <textarea
          ref={textareaRef}
          placeholder="Digite sua pergunta..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isStreaming}
          aria-label="Campo de mensagem"
        />
        <button 
          className="cdx-btn-icon cdx-btn-clear" 
          onClick={handleClear}
          aria-label="Limpar chat"
          type="button"
          disabled={messages.length === 0}
        >
          <TrashIcon />
        </button>
        <button 
          className="cdx-btn-send" 
          disabled={isStreaming || !input.trim()}
          onClick={sendMessage}
          aria-label="Enviar mensagem"
          type="button"
        >
          <SendIcon />
          Enviar
        </button>
      </div>
    </div>
  );
};
